/**
 * Тексти оглядових сторінок — переліку послуг, плану запуску та блогу.
 *
 * Живуть окремо від services.js навмисно: там контент про кожен напрямок,
 * тут — те, що людина читає до того, як обрала напрямок. Питання й відповіді
 * звідси одночасно показуються на сторінці й віддаються пошуковику в розмітці
 * FAQPage, тому текст мусить бути один і той самий.
 */

/**
 * Вхід у сайт за ситуацією клієнта, а не за назвою послуги.
 *
 * Перелік послуг — це внутрішня структура компанії, і за нею людина себе не
 * впізнає: вона звертається не по «документальний супровід», а після відмови
 * в ліцензії або перед виходом із найму. Ці формулювання на сайті вже були,
 * але лежали в полі `audience` на сторінках послуг — тобто їх бачив лише той,
 * хто вже обрав напрямок.
 *
 * Нового змісту тут немає навмисно: кожен пункт — це назва ситуації і
 * посилання на сторінку, де вона розібрана повністю.
 *
 * Формулювання нейтральні й однотипні. Перша редакція була написана від
 * імені клієнта («виходжу з найму», «нам відмовили») і збивалася з особи на
 * особу в межах одного списку; для юридичної компанії така інтонація звучить
 * недоречно.
 */
// Малюнок до кожної ситуації малює SituationGlyph за порядковим номером —
// поля icon тут немає навмисно, інакше воно розійшлося б із малюнками.
export const situations = [
  {
    title: 'Вихід із найму в приватну практику',
    text: 'Потрібно обрати між ФОП і ТОВ, зареєструватися та отримати ліцензію: для прийому пацієнтів самого диплома недостатньо.',
    to: '/poslugy/reyestraciya-medychnogo-biznesu',
    linkLabel: 'Реєстрація та вибір форми'
  },
  {
    title: 'Відкриття клініки з нуля',
    text: 'Приміщення, персонал, оснащення, ліцензія, ЕСОЗ і документи — у порядку, де кожен крок спирається на попередній.',
    to: '/plan-zapusku-kliniky',
    linkLabel: 'План запуску за 6 етапів'
  },
  {
    title: 'Відмова в отриманні ліцензії',
    text: 'Спершу потрібно встановити, що саме не відповідає вимогам, — інакше повторна заява піде з тією ж помилкою.',
    to: '/poslugy/licenzuvannya-medychnoyi-praktyky',
    linkLabel: 'Ліцензування медичної практики'
  },
  {
    title: 'Не подані Відомості за Постановою № 781',
    text: 'Строк минув 8 липня 2026 року. Подати документи все одно потрібно, і що раніше, то менший ризик для ліцензії.',
    to: '/blog/postanova-781-licenzijni-umovy',
    linkLabel: 'Що робити зараз'
  },
  {
    title: 'Перевірка контролюючого органу',
    text: 'Строки на заперечення до акта короткі, а питання закладу одночасно господарські, медичні й про персональні дані.',
    to: '/poslugy/yurydychnyy-autsorsyng',
    linkLabel: 'Юридичний аутсорсинг'
  },
  {
    title: 'Точкове питання чинного закладу',
    text: 'Нова спеціальність, друга адреса, документи під новий напрямок або торговельна марка — без усього циклу.',
    to: '/poslugy',
    linkLabel: 'Перелік напрямків'
  }
];

export const servicesIndexCopy = {
  intro: [
    'Медичний бізнес регулюється щільніше за майже будь-який інший. Щоб прийняти першого пацієнта, закладу потрібні зареєстрований суб’єкт господарювання, приміщення, що відповідає будівельним і санітарним нормам, укомплектований персонал з чинними документами, ліцензія на медичну практику, реєстрація в ЕСОЗ і пакет внутрішньої документації.',
    'Кожен із цих пунктів окремо нескладний — проблема в тому, що вони пов’язані між собою: помилка на реєстрації виявляється на ліцензуванні, а неточність у ліцензійних Відомостях — уже в ЕСОЗ. Ми ведемо всі ці напрямки, тому дивимося на них як на один ланцюг, а не як на окремі послуги з прайсу.',
    'Якщо ви відкриваєте заклад з нуля, знадобляться майже всі й у певному порядку. Якщо клініка вже працює — зазвичай ідеться про один-два.'
  ],
  // Той самий перелік, що в першому абзаці, але поруч із текстом, а не
  // всередині нього: словами це довге перерахування, списком — послідовність.
  checklist: {
    title: 'Що має бути в закладі, щоб прийняти першого пацієнта',
    items: [
      { icon: 'fas fa-sitemap', label: 'Суб’єкт господарювання' },
      { icon: 'fas fa-building', label: 'Приміщення за нормами' },
      { icon: 'fas fa-user', label: 'Персонал з документами' },
      { icon: 'fas fa-id-card', label: 'Ліцензія МОЗ' },
      { icon: 'fas fa-database', label: 'Реєстрація в ЕСОЗ' },
      { icon: 'fas fa-file-contract', label: 'Внутрішні документи' }
    ]
  },
  faq: [
    {
      q: 'З чого почати, якщо я лише думаю відкрити клініку?',
      a: 'З розмови про модель: скільки засновників, який профіль, чи буде приміщення в оренді, чи плануються наймані лікарі. Ці відповіді визначають організаційно-правову форму, КВЕДи й податковий режим, а вони — усе інше. Повну послідовність кроків ми розклали на окремій сторінці плану запуску клініки.'
    },
    {
      q: 'Ви працюєте по всій Україні?',
      a: 'Так. Більшість процедур — реєстрація бізнесу, ліцензування медичної практики, підключення до ЕСОЗ, реєстрація торговельної марки — не потребують фізичної присутності клієнта й ведуться дистанційно.'
    },
    {
      q: 'Ви працюєте лише з клініками?',
      a: 'З медичним бізнесом у широкому сенсі: клініки й медичні центри, лікарі-ФОП із приватною практикою, стоматології, лабораторії, реабілітаційні заклади, кабінети з медичними косметологічними послугами.'
    },
    {
      q: 'Скільки коштують ваші послуги?',
      a: 'Вартість залежить від обсягу: ліцензування клініки з нуля й оновлення Відомостей чинного ліцензіата — різні за трудомісткістю роботи. Ми називаємо ціну після короткої розмови, коли зрозуміло, на якому ви етапі та що вже зроблено.'
    },
    {
      q: 'Чи можна замовити лише одну послугу?',
      a: 'Так. Кожен напрямок працює окремо: чинній клініці частіше потрібне щось одне — ліцензія на нову спеціальність, пакет документів під новий напрямок або торговельна марка.'
    }
  ]
};

export const launchPlanCopy = {
  intro: [
    'Відкриття медичного закладу — це не один документ, а послідовність, у якій кожен наступний крок спирається на попередній. Ліцензію на медичну практику не отримати без приміщення, що відповідає нормам. Приміщення не оцінити, поки не відомо, які саме спеціальності заклад заявлятиме. Спеціальності не заявити без лікарів із чинними документами саме за ними.',
    'Тому найдорожчі помилки на старті — не юридичні, а порядкові: оренда, підписана до аудиту приміщення, або ремонт, зроблений до того, як стало ясно, скільки кабінетів і якої площі потрібно. Нижче — той самий шлях, розкладений на шість етапів. Це не жорсткий графік: частина кроків іде паралельно, а частина відпадає, якщо у вас уже є зареєстрований суб’єкт або приміщення. Але порядок залежностей саме такий.'
  ],
  faq: [
    {
      q: 'Скільки часу займає відкриття клініки з нуля?',
      a: 'Від кількох місяців — за умови, що приміщення вже відповідає нормам, а персонал знайдений. Найдовші ділянки зазвичай дві: приведення приміщення у відповідність, якщо потрібне перепланування, і комплектування штату лікарями з чинними документами саме за заявленими спеціальностями. Самі юридичні процедури рідко бувають вузьким місцем.'
    },
    {
      q: 'Що робити першим — шукати приміщення чи реєструвати бізнес?',
      a: 'Ці кроки йдуть майже паралельно, але аудит приміщення має передувати підписанню договору оренди. Найчастіша дорога помилка — довгостроковий договір на об’єкт, який неможливо привести у відповідність нормам для медичного закладу.'
    },
    {
      q: 'Чи можна відкрити клініку у квартирі або приватному будинку?',
      a: 'Частково — і залежить від багато чого: профілю, площі, наявності окремого входу, можливості вивести приміщення з житлового фонду. Крім того, для окремих видів допомоги розміщення в багатоквартирних будинках прямо обмежене. Це питання вирішується на етапі аудиту локації, а не після ремонту.'
    },
    {
      q: 'Скільки лікарів потрібно для отримання ліцензії?',
      a: 'Кількість не встановлюється абстрактно: під кожну заявлену спеціальність потрібен щонайменше один спеціаліст із чинними документами саме за нею. Окремі вимоги діють щодо керівника закладу та медичного директора.'
    },
    {
      q: 'Чи можна отримати ліцензію на ще не орендоване приміщення?',
      a: 'Ні. Місце провадження діяльності — частина Відомостей, і на нього мають бути правовстановлюючі документи. Але аудит майбутнього приміщення робиться до оренди — саме для того, щоб ліцензування потім не зупинилося.'
    },
    {
      q: 'Чи можна замовити лише один етап?',
      a: 'Так. Якщо клініка вже працює і питання точкове — ліцензія, ЕСОЗ, документи чи торговельна марка — беремося за окремий напрямок без усього циклу.'
    }
  ]
};

export const faqIntro = [
  'Це ті самі питання, які нам ставлять у першій розмові. Ми зібрали їх в одному місці, бо здебільшого людина приходить не по «послугу», а по відповідь: чи потрібна ліцензія ФОП, що робити, якщо відмовили, чи можна відкрити кабінет у квартирі, скільки це триватиме.',
  'Відповіді тут такі самі, як на сторінках послуг, — просто зібрані разом. Ми не пишемо «залежить від обставин» там, де можна відповісти прямо, і не називаємо строків та сум, які насправді залежать від вашого приміщення, персоналу й того, що вже зроблено.',
  'Якщо питання стосується саме вашої ситуації, коротка розмова дасть точнішу відповідь, ніж будь-який загальний текст. Розділи нижче ведуть на сторінки послуг, де та сама тема розібрана докладніше.'
];

export const blogIntro =
  'Медичне законодавство змінюється частіше, ніж встигає доходити до практики: ліцензійні умови оновлюються, вимоги до документації переписуються, з’являються нові строки подання відомостей. Тут ми розбираємо ці зміни так, як вони виглядають із боку керівника клініки: що саме змінилося, кого стосується, що робити і до якого моменту.';

/**
 * Склад ліцензійної справи — для однойменного розділу головної.
 *
 * Нічого нового тут не вигадано: усі шість пунктів уже описані в тексті
 * послуги ліцензування (content/services.js). Тут вони зведені в перелік,
 * бо переліком їх можна показати поруч із малюнком, а суцільним текстом —
 * ні.
 */
export const licenceFile = [
  {
    label: 'Заява до МОЗ',
    text: 'За встановленою формою, з даними суб’єкта господарювання, що збігаються з ЄДР.'
  },
  {
    label: 'Відомості про базу й персонал',
    text: 'Кабінети, площі, обладнання, лікарі. Саме ці Відомості стають мірилом під час перевірки.'
  },
  {
    label: 'Документи на приміщення',
    text: 'Право власності або договір оренди, укладений з урахуванням специфіки медичної діяльності.'
  },
  {
    label: 'Висновок про інклюзивність',
    text: 'Обов’язкова експертиза щодо доступності будівлі для маломобільних груп населення.'
  },
  {
    label: 'Кваліфікація лікарів',
    text: 'Дипломи, сертифікати та посвідчення — чинні на дату подання, а не колись отримані.'
  },
  {
    label: 'Повірки обладнання',
    text: 'Чинна метрологічна повірка засобів вимірювальної техніки за табелем оснащення.'
  }
];
